import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize2, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const VIDEO_SRC = "/media/anreal-video.mp4";
const POSTER = "/images/00-anre-l-residence-vista-exterior.jpg";

const VideoSection = forwardRef(function VideoSection(_props, ref) {
  const videoRef = useRef(null);
  const modalVideoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [started, setStarted] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useImperativeHandle(ref, () => ({
    play: () => {
      const el = videoRef.current;
      if (!el) return;
      el.currentTime = 0;
      el.muted = false;
      setMuted(false);
      el.play().then(() => { setPlaying(true); setStarted(true); }).catch(() => {});
    },
    scrollIntoView: () => {
      document.getElementById("video")?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
  }));

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().then(() => { setPlaying(true); setStarted(true); }).catch(() => {});
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  const openExpanded = () => {
    // Pause inline player when opening modal
    const inline = videoRef.current;
    if (inline && !inline.paused) inline.pause();
    setExpanded(true);
  };

  const closeExpanded = () => {
    const modal = modalVideoRef.current;
    if (modal && !modal.paused) modal.pause();
    setExpanded(false);
  };

  // Autoplay muted when in view (intersection)
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            el.muted = true;
            setMuted(true);
            el.play().then(() => { setPlaying(true); setStarted(true); }).catch(() => {});
          }
        });
      },
      { threshold: 0.5 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [started]);

  return (
    <section id="video" data-testid="video-section" className="relative py-24 lg:py-36 bg-[#0d100e] text-[#F9F9F6] overflow-hidden">
      <div className="absolute inset-0 grain opacity-30" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10 lg:mb-14 items-end">
          <div className="lg:col-span-7">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#F9F9F6]/60 mb-6">Em Movimento</div>
            <h2 className="font-display text-[36px] sm:text-[44px] lg:text-[56px] leading-[1.05] tracking-[-0.02em] text-[#F9F9F6] text-balance">
              Uma visita
              <br />
              <span className="italic-display text-[#D9D6C5]">antes da visita.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <p className="text-[15px] leading-relaxed text-[#F9F9F6]/75 font-light">
              Percorra o ANREÁL Residence em vídeo — jardins, arquitetura, luz, e materiais. Um retrato honesto de como se vive aqui.
            </p>
          </div>
        </div>

        <div className="relative border border-[#F9F9F6]/10 overflow-hidden bg-black">
          <video
            ref={videoRef}
            data-testid="anreal-video"
            src={VIDEO_SRC}
            poster={POSTER}
            playsInline
            loop
            muted
            preload="metadata"
            className="w-full h-auto max-h-[75vh] object-cover"
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onClick={togglePlay}
          />
          {/* Play overlay when paused */}
          {!playing && (
            <button
              type="button"
              data-testid="video-overlay-play"
              onClick={togglePlay}
              aria-label="Reproduzir vídeo"
              className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
            >
              <div className="w-20 h-20 lg:w-24 lg:h-24 rounded-full bg-[#F9F9F6]/95 text-[#1A1A1A] flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                <Play size={30} strokeWidth={1.25} className="ml-1" />
              </div>
            </button>
          )}

          {/* Bottom controls */}
          <div className="absolute bottom-0 inset-x-0 p-4 sm:p-5 flex items-center justify-between gap-3 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none">
            <div className="flex items-center gap-2 pointer-events-auto">
              <button
                type="button"
                data-testid="video-btn-play"
                onClick={togglePlay}
                aria-label={playing ? "Pausar" : "Reproduzir"}
                className="w-11 h-11 flex items-center justify-center bg-[#F9F9F6]/15 hover:bg-[#F9F9F6]/25 backdrop-blur-sm text-[#F9F9F6] transition-colors"
              >
                {playing ? <Pause size={18} strokeWidth={1.5} /> : <Play size={18} strokeWidth={1.5} className="ml-0.5" />}
              </button>
              <button
                type="button"
                data-testid="video-btn-mute"
                onClick={toggleMute}
                aria-label={muted ? "Ativar som" : "Silenciar"}
                className="w-11 h-11 flex items-center justify-center bg-[#F9F9F6]/15 hover:bg-[#F9F9F6]/25 backdrop-blur-sm text-[#F9F9F6] transition-colors"
              >
                {muted ? <VolumeX size={18} strokeWidth={1.5} /> : <Volume2 size={18} strokeWidth={1.5} />}
              </button>
            </div>
            <div className="flex items-center gap-2 pointer-events-auto">
              <div className="hidden sm:block text-[10px] tracking-[0.24em] uppercase text-[#F9F9F6]/70 pr-2">
                ANREÁL · Vídeo Institucional
              </div>
              <button
                type="button"
                data-testid="video-btn-fullscreen"
                onClick={openExpanded}
                aria-label="Expandir vídeo"
                className="w-11 h-11 flex items-center justify-center bg-[#F9F9F6]/15 hover:bg-[#F9F9F6]/25 backdrop-blur-sm text-[#F9F9F6] transition-colors"
              >
                <Maximize2 size={16} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded modal player */}
      <Dialog open={expanded} onOpenChange={(o) => !o && closeExpanded()}>
        <DialogContent
          data-testid="video-modal"
          className="max-w-[98vw] w-[98vw] sm:max-w-[95vw] h-[95vh] p-0 gap-0 border-0 bg-black text-[#F9F9F6] rounded-none overflow-hidden [&>button]:hidden"
        >
          <DialogTitle className="sr-only">ANREÁL Residence — Vídeo institucional</DialogTitle>
          <DialogDescription className="sr-only">Reprodução em ecrã inteiro do vídeo institucional</DialogDescription>
          <div className="relative w-full h-full flex items-center justify-center bg-black">
            <video
              ref={modalVideoRef}
              data-testid="anreal-video-modal"
              src={VIDEO_SRC}
              poster={POSTER}
              controls
              autoPlay
              playsInline
              className="max-w-full max-h-full w-auto h-auto"
            />
            <button
              type="button"
              data-testid="video-modal-close"
              onClick={closeExpanded}
              aria-label="Fechar vídeo"
              className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center bg-black/60 hover:bg-black/80 text-[#F9F9F6] backdrop-blur-sm transition-colors z-10"
            >
              <X size={22} strokeWidth={1.5} />
            </button>
            <div className="absolute top-4 left-4 px-3 py-1.5 bg-black/60 backdrop-blur-sm text-[10px] tracking-[0.24em] uppercase text-[#F9F9F6]/80">
              ANREÁL · Vídeo institucional
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
});

export default VideoSection;
